<script setup>
// Reports → Agents performance. ONE row per agent (a user assigned to inboxes
// with a connected Fangate vault): the accounts they handle (as teal pills) plus
// their overall seller earning + sales count, served by
// Integrations::FangateAgentsPerformanceController. Has its OWN period — fully
// independent from Account performance — including a custom date range.
import { ref, reactive, computed, onMounted, onUnmounted, watch } from 'vue';
import FangateAPI from 'dashboard/api/fangate';

const props = defineProps({
  accountId: { type: [Number, String], default: null },
  isMobile: { type: Boolean, default: false },
});

const DAY = 86400000;
const loading = ref(true);
const agents = ref([]);
const period = ref('thismonth');
const customRange = ref(null); // [from, to] when period === 'custom'
const menuOpen = ref(false);
const calOpen = ref(false);
const expanded = reactive({}); // agent id -> show the per-account breakdown
function toggleAgent(id) { expanded[id] = !expanded[id]; }

const PERIODS = [['today', 'Today'], ['yesterday', 'Yesterday'], ['thisweek', 'This week'], ['lastweek', 'Last week'], ['thismonth', 'This month'], ['alltime', 'All time'], ['custom', 'Custom…']];
const startOfToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };
const startOfMonth = d => new Date(d.getFullYear(), d.getMonth(), 1);
const startOfWeek = d => { const x = new Date(d); const dow = (x.getDay() + 6) % 7; x.setDate(x.getDate() - dow); x.setHours(0, 0, 0, 0); return x; };
function rangeDates() {
  const t = startOfToday(); const end = new Date(t.getTime() + DAY - 1);
  switch (period.value) {
    case 'today': return { from: t, to: end };
    case 'yesterday': return { from: new Date(t.getTime() - DAY), to: new Date(t.getTime() - 1) };
    case 'thisweek': return { from: startOfWeek(t), to: end };
    case 'lastweek': return { from: new Date(startOfWeek(t).getTime() - 7 * DAY), to: new Date(startOfWeek(t).getTime() - 1) };
    case 'thismonth': return { from: startOfMonth(t), to: end };
    case 'custom': return customRange.value ? { from: customRange.value[0], to: customRange.value[1] } : { from: t, to: end };
    default: return { from: null, to: null }; // alltime
  }
}
const fmtD = d => (d ? d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '');
const periodLabel = computed(() => {
  if (period.value === 'custom' && customRange.value) return fmtD(customRange.value[0]) + ' – ' + fmtD(customRange.value[1]);
  return (PERIODS.find(p => p[0] === period.value) || PERIODS[4])[1];
});
const fmtCur = n => new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR' }).format(Number(n || 0));
const initials = name => ((name || '?').trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?');
const hue = name => { let h = 0; for (const c of (name || '')) h = (h * 31 + c.charCodeAt(0)) % 360; return h; };

// ---- calendar (custom range, own state) ------------------------------------
const calView = ref(startOfMonth(new Date()));
const selStart = ref(null);
const selEnd = ref(null);
const calCells = computed(() => {
  const y = calView.value.getFullYear(), m = calView.value.getMonth();
  const startDay = (new Date(y, m, 1).getDay() + 6) % 7;
  const days = new Date(y, m + 1, 0).getDate();
  const today = startOfToday();
  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push({ blank: true, key: 'b' + i });
  for (let d = 1; d <= days; d++) {
    const date = new Date(y, m, d);
    const inRange = selStart.value && selEnd.value && date >= selStart.value && date <= selEnd.value;
    const edge = (selStart.value && +date === +selStart.value) || (selEnd.value && +date === +selEnd.value);
    cells.push({ d, date, key: 'd' + d, muted: date > today, rng: !!inRange, edge: !!edge });
  }
  return cells;
});
const calMonth = computed(() => calView.value.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }));
const calHint = computed(() => (selStart.value ? fmtD(selStart.value) + (selEnd.value ? ' – ' + fmtD(selEnd.value) : ' – …') : 'Pick start date'));
function pickDay(c) {
  if (c.blank || c.muted) return;
  if (!selStart.value || (selStart.value && selEnd.value)) { selStart.value = c.date; selEnd.value = null; }
  else if (c.date < selStart.value) { selStart.value = c.date; }
  else { selEnd.value = c.date; }
}
function applyCustom() {
  if (!selStart.value || !selEnd.value) return;
  customRange.value = [selStart.value, new Date(selEnd.value.getTime() + DAY - 1)];
  period.value = 'custom';
  calOpen.value = false;
  load();
}

// ---- data ------------------------------------------------------------------
async function load() {
  if (!props.accountId) { agents.value = []; loading.value = false; return; }
  loading.value = true;
  const r = rangeDates();
  agents.value = await FangateAPI.getAgentsPerformance(props.accountId, r.from ? r.from.toISOString() : null, r.to ? r.to.toISOString() : null);
  loading.value = false;
}
function closePops() { menuOpen.value = false; calOpen.value = false; }
function togglePeriod() { const o = menuOpen.value || calOpen.value; closePops(); if (!o) menuOpen.value = true; }
function selectPeriod(p) {
  if (p === 'custom') { menuOpen.value = false; calOpen.value = true; return; }
  period.value = p; customRange.value = null; closePops(); load();
}
onMounted(() => { document.addEventListener('click', closePops); load(); });
onUnmounted(() => document.removeEventListener('click', closePops));
watch(() => props.accountId, load);
</script>

<template>
  <div class="fap" :class="{ 'fap-mobile': isMobile }">
    <div class="fap-head">
      <div>
        <div class="fap-eyebrow">Fangate · Live sales</div>
        <h2 class="fap-title">Agents performance</h2>
        <div class="fap-sub">Seller earning &amp; number of sales per agent, across the accounts assigned to them. The same sale counts for every agent assigned to that account — so figures can overlap when an account has more than one agent.</div>
      </div>
      <div class="fap-period">
        <button class="fap-pbtn" @click.stop="togglePeriod">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
          <span>{{ periodLabel }}</span>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>
        </button>
        <div v-if="menuOpen" class="fap-pop" @click.stop>
          <div v-for="p in PERIODS" :key="p[0]" class="fap-mi" :class="{ cur: period === p[0] }" @click="selectPeriod(p[0])">{{ p[1] }}</div>
        </div>
        <div v-if="calOpen" class="fap-pop fap-cal" @click.stop>
          <div class="fap-calhead">
            <button class="fap-navb" aria-label="Previous month" @click="calView = new Date(calView.getFullYear(), calView.getMonth() - 1, 1)"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg></button>
            <span class="fap-calm">{{ calMonth }}</span>
            <button class="fap-navb" aria-label="Next month" @click="calView = new Date(calView.getFullYear(), calView.getMonth() + 1, 1)"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg></button>
          </div>
          <div class="fap-dow"><span>Mo</span><span>Tu</span><span>We</span><span>Th</span><span>Fr</span><span>Sa</span><span>Su</span></div>
          <div class="fap-grid">
            <div v-for="c in calCells" :key="c.key" class="fap-cd" :class="{ mut: c.muted, rng: c.rng, edge: c.edge }" @click="pickDay(c)">{{ c.d }}</div>
          </div>
          <div class="fap-calfoot"><span>{{ calHint }}</span><button class="fap-apply" :disabled="!selStart || !selEnd" @click="applyCustom">Apply</button></div>
        </div>
      </div>
    </div>

    <div v-if="loading" class="fap-empty">Loading…</div>
    <div v-else-if="!agents.length" class="fap-empty">No agents assigned to a Fangate-connected account in this period.</div>
    <div v-else class="fap-tbl">
      <div class="fap-thead">
        <span>Agent</span>
        <span>Accounts</span>
        <span class="r">Seller earning</span>
        <span class="r">Sales</span>
      </div>
      <div v-for="a in agents" :key="a.id" class="fap-card">
        <div class="fap-row fap-arow" :class="{ open: expanded[a.id] }" @click="toggleAgent(a.id)">
          <span class="fap-agent-cell">
            <span class="fap-chev" :class="{ open: expanded[a.id] }" aria-hidden="true"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg></span>
            <span class="fap-ava" :style="{ background: 'hsl(' + hue(a.name) + ',48%,50%)' }">{{ initials(a.name) }}</span>
            <span class="fap-aname">{{ a.name }}</span>
          </span>
          <span class="fap-accts">
            <span v-for="acc in a.accounts" :key="acc.inbox_id" class="fap-acct-pill">{{ acc.name }}</span>
            <span v-if="!a.accounts.length" class="fap-acct-none">—</span>
          </span>
          <span class="r earn">{{ fmtCur(a.overall.earning) }}</span>
          <span class="r cnt">{{ a.overall.sales }}</span>
        </div>
        <template v-if="expanded[a.id]">
          <div v-for="acc in a.accounts" :key="a.id + '-' + acc.inbox_id" class="fap-row fap-subrow">
            <span class="fap-sub-acct">{{ acc.name }}</span>
            <span></span>
            <span class="r earn2">{{ fmtCur(acc.earning) }}</span>
            <span class="r cnt">{{ acc.sales }}</span>
          </div>
          <div v-if="!a.accounts.length" class="fap-row fap-subrow">
            <span class="fap-sub-acct fap-sub-none">No assigned accounts</span><span></span><span></span><span></span>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
.fap { padding: 26px 32px 40px; flex: 1; min-height: 0; overflow-y: auto; }
.fap-mobile { padding: 16px 14px 28px; }
.fap-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 22px; }
.fap-eyebrow { font-size: 10.5px; font-weight: 600; letter-spacing: .16em; text-transform: uppercase; color: var(--gold, #a8842f); margin-bottom: 6px; }
.fap-title { font-family: 'Libre Baskerville', Georgia, serif; font-weight: 400; font-size: 24px; line-height: 1.1; margin: 0; color: var(--ink-strong); }
.fap-sub { font-size: 13px; color: var(--ink-muted); margin-top: 6px; }
.fap-period { position: relative; flex-shrink: 0; }
.fap-pbtn { display: inline-flex; align-items: center; gap: 8px; background: var(--bg-panel); border: 1px solid var(--border-soft); border-radius: 11px; padding: 9px 13px; font: 500 13px 'Inter', sans-serif; color: var(--ink-medium); cursor: pointer; }
.fap-pbtn:hover { border-color: var(--border-medium); }
.fap-pbtn svg:first-child { color: var(--accent, #0d9488); }
.fap-pop { position: absolute; right: 0; top: 46px; z-index: 30; background: var(--bg-panel); border: 1px solid var(--border-medium); border-radius: 14px; box-shadow: var(--shadow-card, 0 8px 26px rgba(31,42,42,.12)); padding: 8px; width: 188px; }
.fap-mi { padding: 9px 12px; border-radius: 9px; cursor: pointer; font-size: 13.5px; color: var(--ink-medium); }
.fap-mi:hover { background: var(--bg-subtle); }
.fap-mi.cur { color: var(--accent, #0d9488); font-weight: 600; }
.fap-empty { padding: 40px; text-align: center; color: var(--ink-muted); font-size: 13.5px; }

/* table — one row per agent */
.fap-tbl { background: transparent; }
/* one card per agent (visual separation between users) */
.fap-card { border: 1px solid var(--border-soft); border-radius: 13px; overflow: hidden; background: var(--bg-panel); box-shadow: var(--shadow-soft, 0 1px 2px rgba(31,42,42,.05)); margin-bottom: 10px; }
.fap-card:last-child { margin-bottom: 0; }
.fap-thead, .fap-row { display: grid; grid-template-columns: 1.5fr 2fr 1fr .6fr; align-items: center; gap: 12px; padding: 12px 16px; }
.fap-thead { font-size: 10.5px; font-weight: 600; letter-spacing: .07em; text-transform: uppercase; color: var(--ink-muted); padding: 2px 16px 9px; }
.fap-thead .r { text-align: right; }
.fap-row { font-size: 13.5px; }
.fap-row .r { text-align: right; font-variant-numeric: tabular-nums; }
.fap-row .earn { color: var(--ink-strong); font-weight: 600; }
.fap-row .cnt { color: var(--ink-medium); }
.fap-agent-cell { display: flex; align-items: center; gap: 9px; min-width: 0; }
.fap-ava { width: 32px; height: 32px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font: 600 12px 'Inter', sans-serif; color: #fff; flex-shrink: 0; }
.fap-aname { font: 600 14px 'Inter', sans-serif; color: var(--ink-strong); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.fap-accts { display: flex; flex-wrap: wrap; gap: 5px; }
.fap-acct-pill { background: var(--accent-tint, rgba(13,148,136,.14)); color: var(--accent-strong, #0f766e); border-radius: 999px; padding: 3px 11px; font: 600 11.5px 'Inter', sans-serif; white-space: nowrap; }
.fap-acct-none { color: var(--ink-muted); }
/* expandable agent row → per-account breakdown */
.fap-arow { cursor: pointer; transition: background .12s; }
.fap-arow:hover { background: var(--bg-subtle); }
.fap-arow.open { background: var(--bg-subtle); }
.fap-chev { display: inline-flex; align-items: center; color: var(--ink-muted); flex-shrink: 0; transition: transform .15s; }
.fap-chev.open { transform: rotate(90deg); }
.fap-subrow { background: var(--bg-panel); border-top: 1px solid var(--border-soft); }
.fap-subrow .earn2 { color: var(--ink-strong); font-weight: 500; }
.fap-sub-acct { padding-left: 47px; color: var(--ink-medium); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.fap-sub-none { font-style: italic; color: var(--ink-muted); }

/* calendar (custom range) */
.fap-cal { width: 300px; }
.fap-calhead { display: flex; align-items: center; justify-content: space-between; padding: 4px 6px 10px; }
.fap-calm { font: 600 13px 'Inter', sans-serif; color: var(--ink-strong); }
.fap-navb { width: 28px; height: 28px; border-radius: 8px; border: 1px solid var(--border-soft); background: var(--bg-panel); color: var(--ink-muted); cursor: pointer; display: inline-flex; align-items: center; justify-content: center; }
.fap-navb:hover { border-color: var(--border-medium); }
.fap-dow { display: grid; grid-template-columns: repeat(7, 1fr); gap: 2px; padding: 0 2px 4px; }
.fap-dow span { text-align: center; font: 600 10px 'Inter', sans-serif; color: var(--ink-muted); text-transform: uppercase; }
.fap-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 2px; }
.fap-cd { aspect-ratio: 1; display: flex; align-items: center; justify-content: center; border-radius: 8px; font-size: 12.5px; color: var(--ink-medium); cursor: pointer; }
.fap-cd:hover { background: var(--bg-subtle); }
.fap-cd.mut { color: var(--border-medium); cursor: default; }
.fap-cd.rng { background: var(--accent-tint, rgba(13,148,136,.14)); }
.fap-cd.edge { background: var(--accent, #0d9488); color: #fff; font-weight: 600; }
.fap-calfoot { display: flex; align-items: center; justify-content: space-between; padding: 10px 4px 2px; margin-top: 6px; border-top: 1px solid var(--border-soft); }
.fap-calfoot span { font-size: 12px; color: var(--ink-muted); }
.fap-apply { border: none; background: var(--accent, #0d9488); color: #fff; border-radius: 9px; padding: 7px 16px; font: 600 12.5px 'Inter', sans-serif; cursor: pointer; }
.fap-apply:disabled { opacity: .5; cursor: default; }
</style>
